
import { ProcessDefinition, AppNode } from './types';
// Fix: Use named import for Edge from reactflow.
import { Edge } from 'reactflow';

// --- Process 1: Employee Onboarding (Parallel Gateway, Email, Forms) ---
const onboardingNodes: AppNode[] = [
  {
    id: 'onb-start',
    type: 'FormStartEvent',
    position: { x: 50, y: 200 },
    data: { 
      label: 'New Hire Trigger', 
      status: 'idle',
      formTitle: 'New Hire Details',
      formSchema: {
        fields: [
            { id: 'f1', label: 'Full Name', key: 'fullName', type: 'text', required: true },
            { id: 'f2', label: 'Department', key: 'dept', type: 'select', options: 'Engineering,Sales,HR', required: true },
            { id: 'f3', label: 'Start Date', key: 'startDate', type: 'date', required: true }
        ]
      }
    },
  },
  {
    id: 'onb-script',
    type: 'ScriptTask',
    position: { x: 250, y: 200 },
    data: { 
      label: 'Generate Email', 
      status: 'idle',
      scriptLanguage: 'javascript',
      script: `// Generate company email\nconst name = variables.fullName || 'User';\nconst dept = variables.dept || 'Corp';\nconst email = name.toLowerCase().replace(' ', '.') + '@peak-systems.com';\n\nreturn {\n  generatedEmail: email,\n  onboardingId: 'ONB-' + Math.floor(Math.random() * 10000)\n};` 
    },
  },
  {
    id: 'onb-split',
    type: 'ParallelGateway',
    position: { x: 450, y: 200 },
    data: { label: 'Split Streams', status: 'idle', isMerging: false },
  },
  {
    id: 'onb-it',
    type: 'UserTask',
    position: { x: 600, y: 100 },
    data: { 
      label: 'IT Provisioning', 
      status: 'idle',
      description: 'Setup laptop and accounts',
      assignee: 'it-support',
      priority: '2',
      formSchema: {
          fields: [
              { id: 'it1', label: 'Laptop Serial', key: 'laptopId', type: 'text', required: true },
              { id: 'it2', label: 'Access Card ID', key: 'cardId', type: 'text', required: true }
          ]
      }
    },
  },
  {
    id: 'onb-hr',
    type: 'UserTask',
    position: { x: 600, y: 300 },
    data: { 
      label: 'HR Orientation', 
      status: 'idle',
      assignee: 'hr-team',
      description: 'Welcome kit and docs',
      formSchema: {
          fields: [
              { id: 'hr1', label: 'Contracts Signed', key: 'docsSigned', type: 'boolean', required: true },
              { id: 'hr2', label: 'Kit Handed Over', key: 'kitGiven', type: 'boolean', required: true }
          ]
      }
    },
  },
  {
    id: 'onb-join',
    type: 'ParallelGateway',
    position: { x: 800, y: 200 },
    data: { label: 'Merge Streams', status: 'idle', isMerging: true },
  },
  {
    id: 'onb-email',
    type: 'ServiceTaskEmail',
    position: { x: 950, y: 200 },
    data: { 
      label: 'Welcome Email', 
      status: 'idle',
      to: '${generatedEmail}',
      subject: 'Welcome to Peak Systems!',
      body: 'Hello ${fullName},\n\nWelcome to the ${dept} team. Your IT setup is complete.\n\nLaptop ID: ${laptopId}\n\nBest,\nHR'
    },
  },
  {
    id: 'onb-end',
    type: 'BlankEndEvent',
    position: { x: 1150, y: 200 },
    data: { label: 'Onboarding Complete', status: 'idle' },
  },
];

const onboardingEdges: Edge[] = [
    { id: 'e-onb-1', source: 'onb-start', target: 'onb-script', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-2', source: 'onb-script', target: 'onb-split', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-3', source: 'onb-split', target: 'onb-it', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-4', source: 'onb-split', target: 'onb-hr', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-5', source: 'onb-it', target: 'onb-join', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-6', source: 'onb-hr', target: 'onb-join', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-7', source: 'onb-join', target: 'onb-email', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e-onb-8', source: 'onb-email', target: 'onb-end', animated: true, style: { stroke: '#94a3b8' } },
];

// --- Process 2: Smart Order Fulfillment (LLM, Exclusive Gateway, Loops) ---
const orderNodes: AppNode[] = [
    {
      id: 'ord-start',
      type: 'MessageStartEvent',
      position: { x: 50, y: 250 },
      data: { label: 'Order Received', status: 'idle', messageName: 'NewOrder' },
    },
    {
        id: 'ord-llm',
        type: 'ServiceTaskLLM',
        position: { x: 250, y: 250 },
        data: { 
            label: 'AI Fraud Check', 
            status: 'idle',
            model: 'gemini-3-flash-preview',
            systemPrompt: 'You are a fraud detection agent. Analyze the order JSON. Return JSON with "riskLevel": "high" or "low" and "reason".',
            userPrompt: 'Analyze this order: ${JSON.stringify(variables.order)}',
            outputVariable: 'fraudAnalysis'
        },
    },
    {
        id: 'ord-gw-risk',
        type: 'XorGateway',
        position: { x: 450, y: 250 },
        data: { 
            label: 'Risk Check', 
            status: 'idle',
            outputs: ['High Risk', 'Low Risk'],
            conditions: [
                { path: 0, expression: "variables.fraudAnalysis?.riskLevel === 'high'" },
                { path: 1, expression: "true" }
            ]
        },
    },
    {
        id: 'ord-review',
        type: 'UserTask',
        position: { x: 550, y: 100 },
        data: { 
            label: 'Manual Review', 
            status: 'idle',
            description: 'Review high risk order',
            formSchema: {
                fields: [
                    { id: 'rv1', label: 'Decision', key: 'reviewDecision', type: 'select', options: 'Approve,Reject', required: true },
                    { id: 'rv2', label: 'Notes', key: 'reviewNotes', type: 'textarea', required: false }
                ]
            }
        },
    },
    {
        id: 'ord-gw-manual',
        type: 'XorGateway',
        position: { x: 750, y: 100 },
        data: { 
            label: 'Decision', 
            status: 'idle',
            outputs: ['Rejected', 'Approved'],
            conditions: [
                { path: 0, expression: "variables.reviewDecision === 'Reject'" },
                { path: 1, expression: "true" }
            ]
        },
    },
    {
        id: 'ord-term',
        type: 'TerminateEndEvent',
        position: { x: 880, y: 40 },
        data: { label: 'Order Rejected', status: 'idle' },
    },
    {
        id: 'ord-pay',
        type: 'ServiceTaskREST',
        position: { x: 750, y: 250 },
        data: { 
            label: 'Charge Card', 
            status: 'idle',
            method: 'POST',
            endpoint: 'https://api.payment-provider.com/charge',
            requestBody: '{"amount": "${order.total}", "token": "${order.paymentToken}"}'
        },
    },
    {
        id: 'ord-timer',
        type: 'IntermediateCatchTimerEvent',
        position: { x: 950, y: 250 },
        data: { label: 'Wait Warehouse', status: 'idle', timerDefinition: 'PT5M' },
    },
    {
        id: 'ord-db',
        type: 'ServiceTaskDB',
        position: { x: 1100, y: 250 },
        data: { label: 'Update Inventory', status: 'idle' },
    },
    {
        id: 'ord-end',
        type: 'MessageEndEvent',
        position: { x: 1300, y: 250 },
        data: { label: 'Order Shipped', status: 'idle' },
    },
];

const orderEdges: Edge[] = [
    { id: 'eo-1', source: 'ord-start', target: 'ord-llm', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'eo-2', source: 'ord-llm', target: 'ord-gw-risk', animated: true, style: { stroke: '#94a3b8' } },
    // High Risk Path
    { id: 'eo-3', source: 'ord-gw-risk', target: 'ord-review', sourceHandle: '0', animated: true, label: 'High Risk', style: { stroke: '#f97316' } },
    { id: 'eo-4', source: 'ord-review', target: 'ord-gw-manual', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'eo-5', source: 'ord-gw-manual', target: 'ord-term', sourceHandle: '0', animated: true, label: 'Rejected', style: { stroke: '#ef4444' } },
    { id: 'eo-6', source: 'ord-gw-manual', target: 'ord-pay', sourceHandle: '1', animated: true, label: 'Approved', style: { stroke: '#10b981' } },
    // Low Risk Path
    { id: 'eo-7', source: 'ord-gw-risk', target: 'ord-pay', sourceHandle: '1', animated: true, label: 'Low Risk', style: { stroke: '#10b981' } },
    // Continuation
    { id: 'eo-8', source: 'ord-pay', target: 'ord-timer', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'eo-9', source: 'ord-timer', target: 'ord-db', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'eo-10', source: 'ord-db', target: 'ord-end', animated: true, style: { stroke: '#94a3b8' } },
];


// --- Process 3: Incident Response (Inclusive Gateway, Boundary Events) ---
const incidentNodes: AppNode[] = [
    {
        id: 'inc-start',
        type: 'SignalStartEvent',
        position: { x: 50, y: 200 },
        data: { label: 'Server Alert', status: 'idle', signalName: 'CPU_SPIKE' },
    },
    {
        id: 'inc-script',
        type: 'ScriptTask',
        position: { x: 200, y: 200 },
        data: { 
            label: 'Parse Logs', 
            status: 'idle',
            script: `// Extract severity from alert payload\nconst severity = variables.alert?.severity || 'LOW';\n\nreturn {\n  isCritical: severity === 'CRITICAL',\n  requiresManager: severity === 'HIGH' || severity === 'CRITICAL'\n};`
        },
    },
    {
        id: 'inc-gw-incl',
        type: 'InclusiveGateway',
        position: { x: 400, y: 200 },
        data: { 
            label: 'Notify Channels', 
            status: 'idle', 
            outputs: ['Email Team', 'Slack Channel'],
            conditions: [
                { path: 0, expression: 'true' },
                { path: 1, expression: 'variables.isCritical' }
            ]
        },
    },
    {
        id: 'inc-email',
        type: 'ServiceTaskEmail',
        position: { x: 600, y: 100 },
        data: { label: 'Email DevOps', status: 'idle', to: 'devops@peak.com', subject: 'Incident Alert' },
    },
    {
        id: 'inc-slack',
        type: 'ServiceTaskREST',
        position: { x: 600, y: 300 },
        data: { label: 'Post to Slack', status: 'idle', endpoint: 'https://slack.com/api/chat.postMessage', method: 'POST' },
    },
    {
        id: 'inc-merge',
        type: 'InclusiveGateway',
        position: { x: 800, y: 200 },
        data: { label: 'Merge', status: 'idle', isMerging: true },
    },
    {
        id: 'inc-resolve',
        type: 'UserTask',
        position: { x: 950, y: 200 },
        data: { label: 'Resolve Incident', status: 'idle', assignee: 'on-call-eng', priority: '1' },
    },
    {
        id: 'inc-boundary-timer',
        type: 'TimerBoundaryEvent',
        // IMPORTANT: Child nodes in ReactFlow must have coordinates relative to the parent (0,0 is parent top-left)
        // Parent 'inc-resolve' is at 950, 200. We want boundary slightly offset.
        position: { x: 120, y: 50 }, 
        data: { label: '1h SLA', status: 'idle', cancelActivity: true, timerDefinition: 'PT1H' },
        parentNode: 'inc-resolve',
        extent: 'parent',
    },
    {
        id: 'inc-end-ok',
        type: 'SignalEndEvent',
        position: { x: 1200, y: 200 },
        data: { label: 'Resolved', status: 'idle' },
    },
    {
        id: 'inc-esc',
        type: 'ServiceTaskEmail',
        position: { x: 1020, y: 380 },
        data: { label: 'Escalate to CTO', status: 'idle', to: 'cto@peak.com', subject: 'SLA BREACHED', priority: 'high' },
    },
    {
        id: 'inc-end-bad',
        type: 'TerminateEndEvent',
        position: { x: 1200, y: 380 },
        data: { label: 'SLA Failed', status: 'idle' },
    },
];

const incidentEdges: Edge[] = [
    { id: 'ei-1', source: 'inc-start', target: 'inc-script', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'ei-2', source: 'inc-script', target: 'inc-gw-incl', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'ei-3', source: 'inc-gw-incl', target: 'inc-email', sourceHandle: '0', animated: true, label: 'Email Team', style: { stroke: '#94a3b8' } },
    { id: 'ei-4', source: 'inc-gw-incl', target: 'inc-slack', sourceHandle: '1', animated: true, label: 'Slack Channel', style: { stroke: '#94a3b8' } },
    { id: 'ei-5', source: 'inc-email', target: 'inc-merge', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'ei-6', source: 'inc-slack', target: 'inc-merge', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'ei-7', source: 'inc-merge', target: 'inc-resolve', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'ei-8', source: 'inc-resolve', target: 'inc-end-ok', animated: true, style: { stroke: '#10b981' } },
    // Boundary flow (SLA Breach)
    // In ReactFlow, we connect the boundary node to the next step
    { id: 'ei-9', source: 'inc-resolve', sourceHandle: 'boundary', target: 'inc-esc', animated: true, label: 'SLA Breach', style: { stroke: '#ef4444', strokeDasharray: '5,5' } },
    { id: 'ei-10', source: 'inc-esc', target: 'inc-end-bad', animated: true, style: { stroke: '#ef4444' } },
];

export const defaultProcesses: ProcessDefinition[] = [
    {
        id: 'proc_onboarding',
        name: 'Employee Onboarding',
        description: 'Standard HR and IT provisioning workflow for new hires.',
        nodes: onboardingNodes,
        edges: onboardingEdges
    },
    {
        id: 'proc_orders',
        name: 'Smart Order Fulfillment',
        description: 'AI-driven order processing with fraud detection and manual review loops.',
        nodes: orderNodes,
        edges: orderEdges
    },
    {
        id: 'proc_incident',
        name: 'Incident Response (SLA)',
        description: 'Critical incident handling with multi-channel notification and SLA escalation.',
        nodes: incidentNodes,
        edges: incidentEdges
    }
];
